import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { getOffset, getScrollLeft, getScrollTop, isWindow, isDocument, getOuterWidth, getOuterHeight } from '@flowio/browser-helpers';
import adjustToOffsetParent from './adjustToOffsetParent';
import fitHorizontally from './fitHorizontally';
import fitVertically from './fitVertically';
import flipHorizontally from './flipHorizontally';
import flipVertically from './flipVertically';
import parseAttachment from './parseAttachment';
import parseConstraint from './parseConstraint';
import parseOffset from './parseOffset';
import parseStyle from './parseStyle';

/**
 * Calculates the coordinates to position an element relative to another.
 * @returns An object with a `position` property with `left` and `top`
 * coordinates that should be set on the target element and a `feedback`
 * property that provides feedback about the position and dimensions of both
 * elements, as well as calculations to their relative position.
 */
export default function calculatePosition(props) {
  var anchorAttachment = props.anchorAttachment,
      anchorElement = props.anchorElement,
      anchorOffset = props.anchorOffset,
      constraint = props.constraint,
      targetElement = props.targetElement,
      targetAttachment = props.targetAttachment,
      targetOffset = props.targetOffset,
      withinElement = props.withinElement;
  var targetWidth = getOuterWidth(targetElement);
  var targetHeight = getOuterHeight(targetElement);
  var anchorWidth = getOuterWidth(anchorElement);
  var anchorHeight = getOuterHeight(anchorElement);

  var _getOffset = getOffset(anchorElement),
      anchorTop = _getOffset.top,
      anchorLeft = _getOffset.left;

  var withinWidth = getOuterWidth(withinElement);
  var withinHeight = getOuterHeight(withinElement);

  var _parseAttachment = parseAttachment(anchorAttachment),
      _parseAttachment2 = _slicedToArray(_parseAttachment, 2),
      anchorAttachmentX = _parseAttachment2[0],
      anchorAttachmentY = _parseAttachment2[1];

  var _parseOffset = parseOffset(anchorOffset, anchorWidth, anchorHeight),
      _parseOffset2 = _slicedToArray(_parseOffset, 2),
      anchorOffsetX = _parseOffset2[0],
      anchorOffsetY = _parseOffset2[1];

  var _parseAttachment3 = parseAttachment(targetAttachment),
      _parseAttachment4 = _slicedToArray(_parseAttachment3, 2),
      targetAttachmentX = _parseAttachment4[0],
      targetAttachmentY = _parseAttachment4[1];

  var _parseOffset3 = parseOffset(targetOffset, targetWidth, targetHeight),
      _parseOffset4 = _slicedToArray(_parseOffset3, 2),
      targetOffsetX = _parseOffset4[0],
      targetOffsetY = _parseOffset4[1];

  var _parseConstraint = parseConstraint(constraint),
      _parseConstraint2 = _slicedToArray(_parseConstraint, 2),
      constraintX = _parseConstraint2[0],
      constraintY = _parseConstraint2[1];

  var targetMarginInlineStart = parseStyle(targetElement, 'marginInlineStart');
  var targetMarginInlineEnd = parseStyle(targetElement, 'marginInlineEnd');
  var targetMarginTop = parseStyle(targetElement, 'marginTop');
  var targetMarginBottom = parseStyle(targetElement, 'marginBottom');
  var isWithinWindow = false;
  var isWithinDocument = false;
  var withinTop = 0;
  var withinLeft = 0;

  if (isWindow(withinElement)) {
    isWithinWindow = true;
  } else if (isDocument(withinElement)) {
    isWithinDocument = true;
  } else {
    var withinOffset = getOffset(withinElement);
    withinTop = withinOffset.top;
    withinLeft = withinOffset.left;
  }

  var withinScrollLeft = getScrollLeft(withinElement);
  var withinScrollTop = getScrollTop(withinElement); // Set initial target element position to the top left corner of the anchor
  // element, relative to the viewport.

  var position = {
    top: anchorTop,
    left: anchorLeft
  }; // Compute target element position without handling constraints,
  // relative to the viewport.

  if (anchorAttachmentX === 'right') {
    position.left += anchorWidth;
  } else if (anchorAttachmentX === 'center') {
    position.left += anchorWidth / 2;
  }

  if (anchorAttachmentY === 'bottom') {
    position.top += anchorHeight;
  } else if (anchorAttachmentY === 'middle') {
    position.top += anchorHeight / 2;
  }

  if (targetAttachmentX === 'right') {
    position.left -= targetWidth;
  } else if (targetAttachmentX === 'center') {
    position.left -= targetWidth / 2;
  }

  if (targetAttachmentY === 'bottom') {
    position.top -= targetHeight;
  } else if (targetAttachmentY === 'middle') {
    position.top -= targetHeight / 2;
  }

  position.left += anchorOffsetX + targetOffsetX;
  position.top += anchorOffsetY + targetOffsetY;
  var context = {
    anchorAttachmentX: anchorAttachmentX,
    anchorAttachmentY: anchorAttachmentY,
    anchorHeight: anchorHeight,
    anchorLeft: anchorLeft,
    anchorOffsetX: anchorOffsetX,
    anchorOffsetY: anchorOffsetY,
    anchorTop: anchorTop,
    anchorWidth: anchorWidth,
    isWithinDocument: isWithinDocument,
    isWithinWindow: isWithinWindow,
    targetAttachmentX: targetAttachmentX,
    targetAttachmentY: targetAttachmentY,
    targetHeight: targetHeight,
    targetMarginBottom: targetMarginBottom,
    targetMarginInlineStart: targetMarginInlineStart,
    targetMarginInlineEnd: targetMarginInlineEnd,
    targetMarginTop: targetMarginTop,
    targetOffsetX: targetOffsetX,
    targetOffsetY: targetOffsetY,
    targetWidth: targetWidth,
    withinHeight: withinHeight,
    withinLeft: withinLeft,
    withinScrollLeft: withinScrollLeft,
    withinScrollTop: withinScrollTop,
    withinTop: withinTop,
    withinWidth: withinWidth
  }; // Adjust computed offset to ensure target element is visible within its
  // constrain element when positioned against the anchor element.

  if (constraintY === 'fit') {
    position = fitVertically(position, context);
  } else if (constraintY === 'flip') {
    position = flipVertically(position, context);
  } else if (constraintY === 'flipfit') {
    position = flipVertically(position, context);
    position = fitVertically(position, context);
  }

  if (constraintX === 'fit') {
    position = fitHorizontally(position, context);
  } else if (constraintX === 'flip') {
    position = flipHorizontally(position, context);
  } else if (constraintX === 'flipfit') {
    position = flipHorizontally(position, context);
    position = fitHorizontally(position, context);
  }

  position = adjustToOffsetParent(targetElement, position); // Determine where the anchor element is located in relation to the target
  // element after it is positioned against it.

  var left = anchorLeft - position.left;
  var right = left + anchorWidth - targetWidth;
  var top = anchorTop - position.top;
  var bottom = top + anchorHeight - targetHeight;
  var feedback = {
    anchor: {
      element: anchorElement,
      top: anchorTop,
      left: anchorLeft,
      height: anchorHeight,
      width: anchorWidth
    },
    target: {
      element: targetElement,
      top: position.top,
      left: position.left,
      width: targetWidth,
      height: targetHeight
    },
    horizontal: 'center',
    vertical: 'middle',
    important: 'vertical'
  };

  if (right < 0) {
    feedback.horizontal = 'left';
  } else if (left > 0) {
    feedback.horizontal = 'right';
  } else {
    feedback.horizontal = 'center';
  }

  if (bottom < 0) {
    feedback.vertical = 'top';
  } else if (top > 0) {
    feedback.vertical = 'bottom';
  } else {
    feedback.vertical = 'middle';
  }

  if (anchorWidth < targetWidth && Math.abs(left + right) < anchorWidth) {
    feedback.horizontal = 'center';
  }

  if (anchorHeight < targetHeight && Math.abs(top + bottom) < anchorHeight) {
    feedback.vertical = 'middle';
  }

  if (Math.max(Math.abs(left), Math.abs(right)) > Math.max(Math.abs(top), Math.abs(bottom))) {
    feedback.important = 'horizontal';
  } else {
    feedback.important = 'vertical';
  }

  return {
    position: position,
    feedback: feedback
  };
}